import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { DailyReadFeedback } from "@app/analytics/types";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import { hasRetryableStatus } from "@app/common/utils";

const TWO_HOURS = 2 * 60 * 60 * 1000;

const useDailyRead = (id?: string) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const latestMetadataQuery = useQuery({
    queryKey: ["dailyRead", user?.id, "latest"],
    queryFn: apiClient.dailyRead.getLatest,
    enabled: !id && Boolean(user),
    retry: (failureCount, error) => {
      return failureCount < 3 && hasRetryableStatus(error);
    },
    staleTime: TWO_HOURS,
  });

  const byIdMetadataQuery = useQuery({
    queryKey: ["dailyRead", id],
    queryFn: () => apiClient.dailyRead.getById(id!),
    enabled: Boolean(id),
    retry: (failureCount, error) => {
      return failureCount < 3 && hasRetryableStatus(error);
    },
  });

  const submitFeedbackMutation = useMutation({
    mutationFn: ({
      id,
      feedback,
    }: {
      id: string;
      feedback: DailyReadFeedback;
    }) => apiClient.dailyRead.submitFeedback(id, feedback),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["dailyRead"] }),
  });

  const query = id ? byIdMetadataQuery : latestMetadataQuery;

  return {
    isLoading: query.isLoading,
    metadata: query.data,
    submitFeedback: submitFeedbackMutation,
  };
};

export default useDailyRead;
